import { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, CheckSquare, Loader2 } from 'lucide-react';
import { SearchResult } from '../../types/search';

interface SearchResultsProps {
  /** Search results to display */
  results: SearchResult[];
  /** Current search query */
  query: string;
  /** Whether the search index is loading */
  isLoading: boolean;
  /** Whether the search index is ready */
  isIndexReady: boolean;
  /** Called when a result is selected */
  onSelect?: () => void;
}

/**
 * Type badge component
 */
function TypeBadge({ type }: { type: 'task' | 'page' }) {
  const styles =
    type === 'task'
      ? 'bg-blue-100 text-blue-700'
      : 'bg-green-100 text-green-700';

  const Icon = type === 'task' ? CheckSquare : FileText;
  const label = type === 'task' ? 'Task' : 'Page';

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${styles}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

/**
 * Individual search result item
 */
function ResultItem({
  result,
  isSelected,
  onClick,
}: {
  result: SearchResult;
  isSelected: boolean;
  onClick: () => void;
}) {
  const itemRef = useRef<HTMLButtonElement>(null);

  // Scroll into view when selected
  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isSelected]);

  return (
    <button
      ref={itemRef}
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-blue-50' : ''
      }`}
      role="option"
      aria-selected={isSelected}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <TypeBadge type={result.type} />
            <h4 className="text-sm font-medium text-gray-900 truncate">
              {result.title}
            </h4>
          </div>
          {result.excerpt && (
            <p className="text-xs text-gray-500 line-clamp-2">{result.excerpt}</p>
          )}
        </div>
        <div className="text-xs text-gray-400 whitespace-nowrap">
          {Math.round(result.score * 100)}% match
        </div>
      </div>
    </button>
  );
}

/**
 * Group results by type for organized display
 */
function groupResults(results: SearchResult[]): {
  tasks: SearchResult[];
  pages: SearchResult[];
} {
  return results.reduce(
    (groups, result) => {
      if (result.type === 'task') {
        groups.tasks.push(result);
      } else {
        groups.pages.push(result);
      }
      return groups;
    },
    { tasks: [] as SearchResult[], pages: [] as SearchResult[] }
  );
}

/**
 * Search results component with keyboard navigation
 */
export default function SearchResults({
  results,
  query,
  isLoading,
  isIndexReady,
  onSelect,
}: SearchResultsProps) {
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [results]);

  // Handle navigation to result
  const handleSelectResult = useCallback(
    (result: SearchResult) => {
      navigate(result.url);
      onSelect?.();
    },
    [navigate, onSelect]
  );

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (results.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < results.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : results.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < results.length) {
            handleSelectResult(results[selectedIndex]);
          }
          break;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [results, selectedIndex, handleSelectResult]);

  // Loading state
  if (isLoading) {
    return (
      <div className="px-4 py-8 text-center text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        <p className="text-sm">Loading search index...</p>
      </div>
    );
  }

  // Index not ready
  if (!isIndexReady) {
    return (
      <div className="px-4 py-8 text-center text-gray-500">
        <p className="text-sm">Search index is initializing...</p>
      </div>
    );
  }

  // No query entered
  if (!query.trim()) {
    return (
      <div className="px-4 py-8 text-center text-gray-500">
        <p className="text-sm">Start typing to search tasks and wiki pages</p>
      </div>
    );
  }

  // No results
  if (results.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-gray-500">
        <p className="text-sm">
          No results found for &quot;{query}&quot;
        </p>
        <p className="text-xs mt-1 text-gray-400">
          Try different keywords or check your spelling
        </p>
      </div>
    );
  }

  // Group results by type
  const { tasks, pages } = groupResults(results);
  let flatIndex = 0;

  return (
    <div
      ref={containerRef}
      className="divide-y divide-gray-100"
      role="listbox"
      aria-label="Search results"
    >
      {/* Tasks section */}
      {tasks.length > 0 && (
        <div>
          <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider">
            Tasks ({tasks.length})
          </div>
          {tasks.map((result) => {
            const currentIndex = flatIndex++;
            return (
              <ResultItem
                key={`task-${result.id}`}
                result={result}
                isSelected={selectedIndex === currentIndex}
                onClick={() => handleSelectResult(result)}
              />
            );
          })}
        </div>
      )}

      {/* Pages section */}
      {pages.length > 0 && (
        <div>
          <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider">
            Wiki Pages ({pages.length})
          </div>
          {pages.map((result) => {
            const currentIndex = flatIndex++;
            return (
              <ResultItem
                key={`page-${result.id}`}
                result={result}
                isSelected={selectedIndex === currentIndex}
                onClick={() => handleSelectResult(result)}
              />
            );
          })}
        </div>
      )}

      {/* Keyboard navigation hint */}
      <div className="px-4 py-2 bg-gray-50 text-xs text-gray-400 flex items-center justify-center gap-4">
        <span>
          <kbd className="px-1 py-0.5 bg-white border rounded text-xs">
            ↑
          </kbd>{' '}
          <kbd className="px-1 py-0.5 bg-white border rounded text-xs">
            ↓
          </kbd>{' '}
          to navigate
        </span>
        <span>
          <kbd className="px-1.5 py-0.5 bg-white border rounded text-xs">
            Enter
          </kbd>{' '}
          to select
        </span>
        <span>
          <kbd className="px-1.5 py-0.5 bg-white border rounded text-xs">
            Esc
          </kbd>{' '}
          to close
        </span>
      </div>
    </div>
  );
}
